import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataGrid } from "@/components/ui/data-grid";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createTenant,
  listProperties,
  listRentPayments,
  listTenants,
  updateTenant,
} from "@/services/dataService";
import { formatCurrency, formatDate, toDateInputValue } from "@/lib/format";

const DAY_MS = 24 * 60 * 60 * 1000;

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  unit: "",
  propertyId: "",
  leaseStartDate: toDateInputValue(Date.now()),
  leaseEndDate: toDateInputValue(Date.now() + 365 * DAY_MS),
  monthlyRent: "",
  rentDueDay: "",
  securityDeposit: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  notes: "",
  isActive: true,
};

function parseNumber(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const next = Number(value);
  return Number.isFinite(next) ? next : undefined;
}

function toTimestamp(value) {
  if (!value) return Date.now();
  const timestamp = new Date(`${value}T00:00:00`).getTime();
  return Number.isFinite(timestamp) ? timestamp : Date.now();
}

function leaseStatus(tenant) {
  if (!tenant.isActive) {
    return { label: "Inactive", variant: "outline" };
  }
  const daysLeft = Math.floor((tenant.leaseEndDate - Date.now()) / DAY_MS);
  if (daysLeft < 0) {
    return { label: "Lease expired", variant: "destructive" };
  }
  if (daysLeft <= 60) {
    return { label: `Renewal in ${daysLeft}d`, variant: "secondary" };
  }
  return { label: "Active lease", variant: "default" };
}

function tenantToForm(tenant) {
  return {
    firstName: tenant.firstName || "",
    lastName: tenant.lastName || "",
    email: tenant.email || "",
    phone: tenant.phone || "",
    unit: tenant.unit || "",
    propertyId: tenant.propertyId || "",
    leaseStartDate: toDateInputValue(tenant.leaseStartDate),
    leaseEndDate: toDateInputValue(tenant.leaseEndDate),
    monthlyRent: String(tenant.monthlyRent ?? ""),
    rentDueDay: tenant.rentDueDay ? String(tenant.rentDueDay) : "",
    securityDeposit: String(tenant.securityDeposit ?? ""),
    emergencyContactName: tenant.emergencyContactName || "",
    emergencyContactPhone: tenant.emergencyContactPhone || "",
    notes: tenant.notes || "",
    isActive: Boolean(tenant.isActive),
  };
}

export default function Tenants() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | tenant object
  const [form, setForm] = useState(EMPTY_FORM);

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const tenantsQuery = useQuery({
    queryKey: ["tenants", userId],
    queryFn: () => listTenants(),
    enabled: Boolean(userId),
  });

  const paymentsQuery = useQuery({
    queryKey: ["rentPayments", userId],
    queryFn: () => listRentPayments(),
    enabled: Boolean(userId),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing === "new" ? createTenant(payload) : updateTenant({ id: editing._id, ...payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenants", userId] });
      setEditing(null);
      toast({ title: editing === "new" ? "Tenant added" : "Tenant updated" });
    },
    onError: (error) =>
      toast({ title: "Could not save tenant", description: error.message, variant: "destructive" }),
  });

  const propertyMap = useMemo(
    () =>
      Object.fromEntries(
        (propertiesQuery.data || []).map((property) => [
          property._id,
          property.name || `${property.address}, ${property.city}`,
        ])
      ),
    [propertiesQuery.data]
  );

  const lastPaymentByTenant = useMemo(() => {
    const map = new Map();
    for (const payment of paymentsQuery.data || []) {
      if (!payment.tenantId || payment.status !== "completed") continue;
      const existing = map.get(payment.tenantId);
      if (!existing || payment.paymentDate > existing.paymentDate) {
        map.set(payment.tenantId, payment);
      }
    }
    return map;
  }, [paymentsQuery.data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (tenantsQuery.data || [])
      .filter((tenant) => {
        if (propertyFilter !== "all" && tenant.propertyId !== propertyFilter) return false;
        if (!term) return true;
        return [
          tenant.firstName,
          tenant.lastName,
          tenant.email,
          tenant.phone,
          propertyMap[tenant.propertyId],
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term));
      })
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.leaseEndDate - b.leaseEndDate);
  }, [tenantsQuery.data, searchTerm, propertyFilter, propertyMap]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const openEdit = (tenant) => {
    setForm(tenantToForm(tenant));
    setEditing(tenant);
  };

  const submit = () => {
    saveMutation.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      unit: form.unit.trim() || undefined,
      propertyId: form.propertyId,
      leaseStartDate: toTimestamp(form.leaseStartDate),
      leaseEndDate: toTimestamp(form.leaseEndDate),
      monthlyRent: parseNumber(form.monthlyRent) || 0,
      rentDueDay: parseNumber(form.rentDueDay),
      securityDeposit: parseNumber(form.securityDeposit) || 0,
      isActive: form.isActive,
      emergencyContactName: form.emergencyContactName.trim() || undefined,
      emergencyContactPhone: form.emergencyContactPhone.trim() || undefined,
      notes: form.notes.trim() || undefined,
    });
  };

  const field = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const columns = [
    {
      key: "name",
      header: "Tenant",
      sortable: true,
      sortValue: (t) => `${t.firstName} ${t.lastName}`,
      render: (t) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">
            {t.firstName} {t.lastName}
          </p>
          <p className="truncate text-xs text-muted-foreground">{t.email || t.phone || "—"}</p>
        </div>
      ),
    },
    {
      key: "property",
      header: "Property",
      sortable: true,
      sortValue: (t) => propertyMap[t.propertyId] || "",
      render: (t) => (
        <span>
          {propertyMap[t.propertyId] || "—"}
          {t.unit ? <span className="text-muted-foreground"> · {t.unit}</span> : null}
        </span>
      ),
    },
    {
      key: "monthlyRent",
      header: "Rent",
      align: "right",
      sortable: true,
      render: (t) => formatCurrency(t.monthlyRent),
    },
    {
      key: "rentDueDay",
      header: "Due day",
      align: "center",
      sortable: true,
      sortValue: (t) => t.rentDueDay || 1,
      render: (t) => `Day ${t.rentDueDay || 1}`,
    },
    {
      key: "leaseEndDate",
      header: "Lease ends",
      sortable: true,
      render: (t) => formatDate(t.leaseEndDate),
    },
    {
      key: "lastPayment",
      header: "Last payment",
      sortValue: (t) => lastPaymentByTenant.get(t._id)?.paymentDate || 0,
      render: (t) => {
        const payment = lastPaymentByTenant.get(t._id);
        return payment
          ? `${formatCurrency(payment.amount)} · ${formatDate(payment.paymentDate)}`
          : "—";
      },
    },
    {
      key: "status",
      header: "Status",
      sortValue: (t) => (t.isActive ? 1 : 0),
      render: (t) => {
        const status = leaseStatus(t);
        return <Badge variant={status.variant}>{status.label}</Badge>;
      },
    },
  ];

  const activeRent = filtered.reduce(
    (sum, t) => sum + (t.isActive ? t.monthlyRent || 0 : 0),
    0
  );
  columns[0].footer = `${filtered.length} tenant${filtered.length === 1 ? "" : "s"}`;
  columns[2].footer = formatCurrency(activeRent);

  return (
    <PageLayout
      title="Tenants"
      eyebrow="People"
      subtitle="Leases, renewals, contacts, and payment history at a glance."
      onRefresh={() => {
        tenantsQuery.refetch();
        paymentsQuery.refetch();
      }}
      isRefreshing={tenantsQuery.isFetching}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <select
              value={propertyFilter}
              onChange={(event) => setPropertyFilter(event.target.value)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
            >
              <option value="all">All properties</option>
              {(propertiesQuery.data || []).map((property) => (
                <option key={property._id} value={property._id}>
                  {property.name || property.address}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Add tenant
          </Button>
        </div>

        <DataGrid
          columns={columns}
          rows={filtered}
          getRowId={(t) => t._id}
          onRowClick={openEdit}
          initialSort={{ key: "status", dir: "desc" }}
          emptyState={
            searchTerm || propertyFilter !== "all"
              ? "No matching tenants."
              : "Add your tenants to track leases, renewals, and payments."
          }
        />

        <p className="px-1 text-xs text-muted-foreground">
          Click any tenant to edit lease, contact, and deposit details.
        </p>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add tenant" : "Edit tenant"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>First name</Label>
                <Input value={form.firstName} onChange={field("firstName")} />
              </div>
              <div className="space-y-1">
                <Label>Last name</Label>
                <Input value={form.lastName} onChange={field("lastName")} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={field("email")} />
              </div>
              <div className="space-y-1">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={field("phone")} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Property</Label>
                <select
                  value={form.propertyId}
                  onChange={field("propertyId")}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">Select property</option>
                  {(propertiesQuery.data || []).map((property) => (
                    <option key={property._id} value={property._id}>
                      {property.name || property.address}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Unit (optional)</Label>
                <Input value={form.unit} onChange={field("unit")} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Lease start</Label>
                <Input type="date" value={form.leaseStartDate} onChange={field("leaseStartDate")} />
              </div>
              <div className="space-y-1">
                <Label>Lease end</Label>
                <Input type="date" value={form.leaseEndDate} onChange={field("leaseEndDate")} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Monthly rent</Label>
                <Input type="number" min="0" value={form.monthlyRent} onChange={field("monthlyRent")} />
              </div>
              <div className="space-y-1">
                <Label>Rent due on day</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  placeholder="1"
                  value={form.rentDueDay}
                  onChange={field("rentDueDay")}
                />
                <p className="text-xs text-muted-foreground">
                  Day of the month rent is expected — used in the cash-flow forecast.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Security deposit</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.securityDeposit}
                  onChange={field("securityDeposit")}
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Emergency contact</Label>
                <Input
                  value={form.emergencyContactName}
                  onChange={field("emergencyContactName")}
                />
              </div>
              <div className="space-y-1">
                <Label>Emergency phone</Label>
                <Input
                  value={form.emergencyContactPhone}
                  onChange={field("emergencyContactPhone")}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={field("notes")} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, isActive: event.target.checked }))
                }
                className="h-4 w-4 rounded border"
              />
              Active tenant
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                saveMutation.isPending ||
                !form.firstName.trim() ||
                !form.propertyId ||
                !parseNumber(form.monthlyRent)
              }
            >
              {saveMutation.isPending
                ? "Saving..."
                : editing === "new"
                  ? "Add tenant"
                  : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
